/**
 * Scroll-Motion-Helfer.
 *
 * Jede Szene bekommt von der Engine `--l` (0..1, Fortschritt im Kapitel) als
 * CSS-Variable. Die Helfer hier bauen daraus Style-Objekte mit calc(), damit
 * pro Frame nur eine Variable geschrieben wird und React nicht neu rendert.
 * `--motion` ist 0 bei prefers-reduced-motion, dann bleibt nur das Einblenden.
 */

export const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v))
export const lerp = (a, b, t) => a + (b - a) * t
export const seg = (t, a, b) => clamp((t - a) / (b - a))
export const smooth = (t) => t * t * (3 - 2 * t)

const n = (v) => Number(v.toFixed(4))
const k = ([a, b]) => `clamp(0, (var(--l) - ${n(a)}) / ${n(b - a)}, 1)`
const ease = (name) =>
  `calc(var(--${name}k) * var(--${name}k) * (3 - 2 * var(--${name}k)))`

/** Einblenden im Bereich `at` von --l, optional Ausblenden im Bereich `out`. */
export function anim({
  at = [0, 0.1],
  out,
  x = 0,
  y = 20,
  s = 1,
  r = 0,
  ox = 0,
  oy = -16
} = {}) {
  return {
    '--ak': k(at),
    '--a': ease('a'),
    '--bk': out ? k(out) : '0',
    '--b': ease('b'),
    opacity: 'calc(var(--a) * (1 - var(--b)))',
    transform:
      `translate(calc(((1 - var(--a)) * ${x}px + var(--b) * ${ox}px) * var(--motion)), ` +
      `calc(((1 - var(--a)) * ${y}px + var(--b) * ${oy}px) * var(--motion))) ` +
      `scale(calc(${n(s)} + ${n(1 - s)} * var(--a))) ` +
      `rotate(calc((1 - var(--a)) * ${r}deg * var(--motion)))`
  }
}

/** Fortschritt eines Bereichs als `--<name>` (geglättet), für eigene calc()-Styles. */
export const pv = (name, at) => ({
  [`--${name}k`]: k(at),
  [`--${name}`]: ease(name)
})

/** Kurzform von pv mit dem Namen `p`. */
export const prog = (at) => pv('p', at)

/** Bewegung von `from` nach `to` (px), sichtbar während der Fahrt. */
export function travel({ at, from, to, keep = false }) {
  const [x0, y0] = from
  const [x1, y1] = to
  return {
    '--tk': k(at),
    '--t': ease('t'),
    opacity: keep
      ? 'clamp(0, var(--tk) * 8, 1)'
      : 'calc(clamp(0, var(--tk) * 8, 1) * clamp(0, (1 - var(--tk)) * 8, 1))',
    transform: `translate(calc(${x0}px + ${x1 - x0}px * var(--t)), calc(${y0}px + ${y1 - y0}px * var(--t)))`
  }
}

/** Gleichmäßig verteilte Bereiche, z.B. für gestaffelte Listen. */
export const stagger = (start, step, len, i) => [
  start + step * i,
  start + step * i + len
]
